/* eslint-disable max-lines */
import { CloudIcon } from 'lucide-react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import type { Project } from '@/lib/queries/project';
import { type CloudResource } from '@/lib/queries/resource';

import { Coverage } from './Coverage';
import { ImpactNotAvailable } from './Coverage/ImpactNotAvailable';
import { ResourceHistory } from './History';
import { type DataItemType, ResourceData } from './ResourceData';
import ResourceHeader from './ResourceHeader';
import { UpdatesInterval } from './UpdatesInterval';
interface ResourceProps {
  resource: CloudResource;
  mainProject: Project;
  resourceNodeId: string;
  from?: string;
  to?: string;
}
const flattenDataForResourceProperties = (
  resource: CloudResource,
  t: IntlShape['formatMessage'],
): DataItemType[] => {
  return [
    ...(resource.region === undefined || resource.regionHashedID === undefined
      ? []
      : [
          {
            label: t({ id: 'resource.instance.details.region.label' }),
            value: resource.region,
            link: {
              to: '/resource/$resourceId' as const,
              params: {
                resourceId: resource.regionHashedID,
              },
              newTab: false as const,
              description: t({
                id: 'resource.instance.details.region.description',
              }),
            },
          },
        ]),
    ...(resource.account === undefined || resource.accountHashedID === undefined
      ? []
      : [
          {
            label: t({ id: 'resource.instance.details.account.label' }),
            value: resource.account,
            link: {
              to: '/resource/$resourceId' as const,
              params: {
                resourceId: resource.accountHashedID,
              },
              newTab: false as const,
              description: t({
                id: 'resource.instance.details.account.description',
              }),
            },
          },
        ]),
    ...(resource.serviceHashedID === undefined
      ? []
      : [
          {
            label: t({ id: 'resource.instance.details.service.label' }),
            value: resource.service,
            link: {
              to: '/resource/$resourceId' as const,
              params: {
                resourceId: resource.serviceHashedID,
              },
              newTab: false as const,
              description: t({
                id: 'resource.instance.details.service.description',
              }),
            },
          },
        ]),
    {
      label: t({ id: 'resource.instance.details.resourceType.label' }),
      value: `${resource.resourceType}`,
    },
  ];
};
// eslint-disable-next-line complexity
function flattenDataForResourceDefinition(
  resource: CloudResource,
  t: IntlShape['formatMessage'],
): DataItemType[] {
  return [
    ...(resource.codeDefinitionName === undefined ||
    resource.codeGithubURL === undefined
      ? []
      : [
          {
            label: t({ id: 'resource.instance.definition.resourceCode.label' }),
            value: resource.codeDefinitionName,
            link: {
              href: resource.codeGithubURL,
              newTab: true as const,
            },
          },
        ]),
    ...(resource.codeLastUpdatedURL === undefined ||
    resource.codeCreatedURL === undefined
      ? []
      : [
          {
            label: t({ id: 'resource.instance.definition.codeCreated.label' }),
            value: resource.codeCreatedAt,
            secondaryValue: resource.createCommitter,
            link: {
              href: resource.codeCreatedURL,
              newTab: true as const,
            },
          },
          {
            label: t({ id: 'resource.instance.definition.codeUpdated.label' }),
            value: resource.codeLastUpdatedAt,
            secondaryValue: resource.updateCommitter,
            link: {
              href: resource.codeLastUpdatedURL,
              newTab: true as const,
            },
          },
        ]),
    ...(resource.moduleName === undefined ||
    resource.moduleGithubURL === undefined
      ? []
      : [
          {
            label: t({ id: 'resource.instance.definition.moduleCode.label' }),
            value: resource.moduleName,
            link: {
              href: resource.moduleGithubURL,
              newTab: true as const,
            },
          },
        ]),
    ...(resource.moduleName === undefined &&
    resource.codeDefinitionName === undefined
      ? [
          {
            label: t({ id: 'resource.instance.definition.noCode.label' }),
            value: resource.scannedInCloud
              ? t({
                  id: 'resource.instance.definition.noCode.value.notManaged',
                })
              : t({ id: 'resource.instance.definition.noCode.value.na' }),
          },
        ]
      : []),
    {
      label: t({ id: 'resource.instance.definition.awsURL.label' }),
      value:
        resource.awsURL === '' ? t({ id: 'global.noValue' }) : resource.awsURL,
      link:
        resource.awsURL === ''
          ? undefined
          : {
              href: resource.awsURL,
              newTab: true as const,
            },
    },
  ];
}
export const ResourceInstance = ({
  resource,
  mainProject,
  from,
  to,
}: ResourceProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const detailsConfig = flattenDataForResourceProperties(
    resource,
    formatMessage,
  );
  const codeDefinitionConfig = flattenDataForResourceDefinition(
    resource,
    formatMessage,
  );

  return (
    <div className="mb-6">
      <div className="flex flex-row justify-between items-center">
        <ResourceHeader
          title={resource.resourceId}
          subtitle={resource.label.toUpperCase()}
          deletedAt={resource.deletedAt}
          Icon={<CloudIcon className="text-blue-500 h-16 w-16" />}
        />
        <UpdatesInterval hashedID={resource.hashedID} from={from} to={to} />
      </div>
      <div className="my-8 flex flex-row w-full space-x-16">
        <div className="grow basis-0">
          <h2 className="text-2xl mb-4">
            <FormattedMessage id="resource.instance.details.title" />
          </h2>
          <ResourceData data={detailsConfig} />
        </div>
        <div className="grow basis-0">
          <h2 className="text-2xl mb-4">
            <FormattedMessage id="resource.instance.definition.title" />{' '}
          </h2>
          <ResourceData data={codeDefinitionConfig} />
        </div>
      </div>
      <ResourceHistory
        mainProject={mainProject}
        hashedID={resource.hashedID}
        from={from}
        to={to}
      />
      {resource.scannedInCloud ? (
        <Coverage
          mainProject={mainProject}
          hashedID={resource.hashedID}
          from={from}
          to={to}
        />
      ) : (
        <ImpactNotAvailable service={resource.service} />
      )}
    </div>
  );
};
